// src/components/EventForm.js - Standardized event naming
import React, { useState, useEffect } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { useAuth } from '../auth/AuthContext';
import auditLogger from '../utils/auditLogger';
import { normalizeDancer, normalizeEvent } from '../utils/fieldCompatibility';
import './EventForm.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const ROUND_TYPES = [
  'Round 1',
  'Round 2',
  'Round 3',
  'Round 4',
  'Round 5',
  'Round 6',
  'Round 7',
  'Round 8',
  'Quarter-Final',
  'Semi-Final',
  'Final'
];

const AGE_GROUPS = [
  'Juvenile 1',
  'Juvenile 2',
  'Junior 1',
  'Junior 2',
  'Youth',
  'Under 21',
  'Adult',
  'Senior 1',
  'Senior 2',
  'Senior 3',
  'Senior 4'
];

const LEVELS = [
  'Level 1',
  'Level 2',
  'Level 3',
  'Level 4',
  'Rising-Star',
  'Championship',
  'Professional'
];

const DANCER_TYPES = [
  'Couple',
  'Solo',
  'Pro-Am',
  'All Girls',
  'Formation',
  'Show Dance'
];

const DANCE_STYLES = [
  'Standard',
  'Latin'
];

const DEFAULT_LATIN_DANCES = [
  'Samba',
  'Cha Cha Cha',
  'Rumba',
  'Paso Doble',
  'Jive'
];

const DEFAULT_STANDARD_DANCES = [
  'Waltz',
  'Tango',
  'Slow Foxtrot',
  'Viennese Waltz',
  'Quickstep'
];

const NON_DANCE_EVENT_TYPES = [
  { value: 'break', label: 'Break', defaultDuration: 15 },
  { value: 'lunch', label: 'Lunch', defaultDuration: 45 },
  { value: 'entertainment', label: 'Entertainment', defaultDuration: 20 },
  { value: 'custom', label: 'Custom Event', defaultDuration: 30 }
];

const EventForm = ({ onCreateEvent, existingEvents = [] }) => {
  const { selectedCompetition } = useCompetition();
  const { currentUser } = useAuth();
  const [showForm, setShowForm] = useState(false);

  // Event type selection
  const [eventType, setEventType] = useState('dance'); // 'dance' or 'non-dance'

  // Debug: Log existingEvents whenever it changes
  useEffect(() => {
    console.log('📊 EventForm: existingEvents updated:', {
      count: existingEvents.length,
      events: existingEvents.slice(0, 2) // Show first 2 events for debugging
    });
  }, [existingEvents]);

  // Event name components (for dance events)
  const [ageGroup, setAgeGroup] = useState('Adult');
  const [level, setLevel] = useState('Championship');
  const [dancerType, setDancerType] = useState('Couple');
  const [danceStyle, setDanceStyle] = useState('Latin');
  const [roundType, setRoundType] = useState('TBD');

  // Non-dance event properties
  const [nonDanceEventType, setNonDanceEventType] = useState('break');
  const [nonDanceEventName, setNonDanceEventName] = useState('');
  const [customDuration, setCustomDuration] = useState(15); // Default 15 minutes

  const [dances, setDances] = useState([...DEFAULT_LATIN_DANCES]);
  const [newDance, setNewDance] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [availableCouples, setAvailableCouples] = useState([]);
  const [selectedCouples, setSelectedCouples] = useState([]);
  const [coupleSearch, setCoupleSearch] = useState('');
  const [fetchedEvents, setFetchedEvents] = useState([]);

  // Auto-generate event name based on selections
  const generateEventName = () => {
    if (eventType === 'non-dance') {
      if (nonDanceEventName.trim()) {
        return nonDanceEventName.trim();
      }

      // Auto-generate name based on type
      const eventTypeInfo = NON_DANCE_EVENT_TYPES.find(type => type.value === nonDanceEventType);
      return eventTypeInfo ? eventTypeInfo.label : 'Non-Dance Event';
    }

    // Dance event name generation
    const parts = [ageGroup, level];

    // Include dancer type in name if it's not Couple (since Couple is the default)
    if (dancerType !== 'Couple') {
      parts.push(dancerType);
    }

    parts.push(danceStyle);

    return parts.join(' ');
  };

  // Get default duration for non-dance events
  const getNonDanceEventDuration = () => {
    const eventTypeInfo = NON_DANCE_EVENT_TYPES.find(type => type.value === nonDanceEventType);
    return eventTypeInfo ? eventTypeInfo.defaultDuration : 15;
  };

  // Generate section field in format Age Group Level Style (for dance events)
  const generateSection = () => {
    if (eventType === 'non-dance') {
      return 'Non-Dance';
    }
    return `${ageGroup} ${level} ${danceStyle}`;
  };

  // Update dances when style changes (for dance events)
  useEffect(() => {
    if (eventType === 'dance' && danceStyle === 'Latin') {
      setDances([...DEFAULT_LATIN_DANCES]);
    } else if (eventType === 'dance') {
      setDances([...DEFAULT_STANDARD_DANCES]);
    }
  }, [danceStyle, eventType]);

  // Update custom duration when non-dance event type changes
  useEffect(() => {
    if (eventType === 'non-dance') {
      setCustomDuration(getNonDanceEventDuration());
    }
  }, [nonDanceEventType, eventType]);

  // Update non-dance event name when type changes (if no custom name is set)
  useEffect(() => {
    if (eventType === 'non-dance' && !nonDanceEventName.trim()) {
      const eventTypeInfo = NON_DANCE_EVENT_TYPES.find(type => type.value === nonDanceEventType);
      if (eventTypeInfo && nonDanceEventType !== 'custom') {
        setNonDanceEventName(eventTypeInfo.label);
      }
    }
  }, [nonDanceEventType, eventType, nonDanceEventName]);

  // Check for duplicates whenever form fields change
  useEffect(() => {
    if (showForm && existingEvents.length > 0) {
      const duplicate = getDuplicateEvent();
      if (duplicate) {
        setDuplicateWarning({
          message: 'An event with these exact specifications already exists',
          existingEvent: duplicate,
          itemNumber: duplicate.itemNumber
        });
      } else {
        setDuplicateWarning(null);
      }
    }
  }, [ageGroup, level, dancerType, danceStyle, roundType, showForm, existingEvents]);

  // Helper function to properly format dancer names
  const formatDancerName = (couple) => {
    if (couple.name && couple.name.trim() && couple.name !== 'undefined' && !couple.name.includes('undefined')) {
      return couple.name.trim();
    }

    if (couple.dancerType === 'solo') {
      return couple.leadName || 'Unknown Dancer';
    } else if (couple.dancerType === 'team') {
      if (couple.teamMembers && couple.teamMembers.length > 0) {
        return couple.teamMembers.join(', ');
      }
      return couple.leadName || 'Unknown Team';
    } else {
      if (couple.leadName && couple.followName && couple.followName !== 'undefined') {
        return `${couple.leadName} & ${couple.followName}`;
      } else if (couple.leadName && couple.leadName !== 'undefined') {
        return couple.leadName;
      } else if (couple.followName && couple.followName !== 'undefined') {
        return couple.followName;
      }
    }

    return `Dancer ${couple.clothNumber}`;
  };

  // Check if a dancer is already allocated to an event with the same dancer type and style
  const checkAllocationConflict = (coupleId) => {
    const couple = availableCouples.find(c => c.id === coupleId);
    if (!couple) return null;

    const coupleDancerType = couple.dancerType || 'couple';

    // Check all existing events for conflicts
    for (const event of fetchedEvents) {
      // Skip if event doesn't have the same dancer type and dance style
      if (event.dancerType !== dancerType || event.danceStyle !== danceStyle) {
        continue;
      }

      // Handle dancers field - it might be a JSON string, array, or undefined
      let eventDancers = [];
      if (event.dancers) {
        if (typeof event.dancers === 'string') {
          try {
            eventDancers = JSON.parse(event.dancers);
          } catch (error) {
            eventDancers = [];
          }
        } else if (Array.isArray(event.dancers)) {
          eventDancers = event.dancers;
        }
      }

      // Check if this dancer is already allocated to this event
      const isAllocated = eventDancers.some(dancer => dancer.coupleId === coupleId);
      if (isAllocated) {
        return event.name;
      }
    }

    return null;
  };

  // Filter out already allocated dancers for the current dancer type and style
  const getFilteredAvailableCouples = () => {
    return availableCouples.filter(couple => {
      const conflict = checkAllocationConflict(couple.id);
      return !conflict;
    });
  };

  // Filter couples by search term and availability
  const filteredCouples = getFilteredAvailableCouples().filter(couple => {
    const searchLower = coupleSearch.toLowerCase();
    const name = formatDancerName(couple).toLowerCase();
    const clothNumber = couple.clothNumber?.toString() || '';

    return name.includes(searchLower) || clothNumber.includes(searchLower);
  });

  // Fetch available couples and existing events when form is shown
  useEffect(() => {
    if (showForm) {
      const fetchCouplesAndEvents = async () => {
        try {
          // Fetch couples (all dancers available for allocation)
          const couplesResponse = await fetch(`https://${_host}:6003/api/${_company}/couples`);
          if (couplesResponse.ok) {
            const couplesData = await couplesResponse.json();
            // Normalize couples to handle both camelCase and snake_case field names
            const normalizedCouples = Array.isArray(couplesData) ? couplesData.map(couple => normalizeDancer(couple)) : [];
            setAvailableCouples(normalizedCouples);
          }

          // Fetch existing events for validation
          const eventsResponse = await fetch(`https://${_host}:6003/api/${_company}/events`);
          if (eventsResponse.ok) {
            const eventsData = await eventsResponse.json();
            setFetchedEvents(Array.isArray(eventsData) ? eventsData : []);
          }
        } catch (err) {
          console.error('Error fetching data:', err);
          setAvailableCouples([]);
          setFetchedEvents([]);
        }
      };

      fetchCouplesAndEvents();
    }
  }, [showForm]);

  const toggleForm = () => {
    setShowForm(!showForm);
    if (!showForm) {
      resetForm();
    }
  };

  const resetForm = () => {
    setEventType('dance');
    setAgeGroup('Adult');
    setLevel('Championship');
    setDancerType('Couple');
    setDanceStyle('Latin');
    setDances([...DEFAULT_LATIN_DANCES]);
    setNewDance('');

    // Reset non-dance event fields
    setNonDanceEventType('break');
    setNonDanceEventName('');
    setCustomDuration(15);

    setError(null);
    setSuccess(false);
    setSelectedCouples([]);
    setCoupleSearch('');
    setDuplicateWarning(null);
  };

  const handleAddDance = () => {
    if (newDance.trim() && !dances.includes(newDance.trim())) {
      setDances([...dances, newDance.trim()]);
      setNewDance('');
    }
  };

  const handleRemoveDance = (index) => {
    const updatedDances = [...dances];
    updatedDances.splice(index, 1);
    setDances(updatedDances);
  };

  const handleCoupleToggle = (coupleId) => {
    setSelectedCouples(prev => {
      if (prev.includes(coupleId)) {
        return prev.filter(id => id !== coupleId);
      } else {
        // Check for allocation conflicts before adding
        const conflict = checkAllocationConflict(coupleId);
        if (conflict) {
          alert(`Allocation conflict: This dancer is already allocated to "${conflict}" with the same dancer type and style.`);
          return prev; // Don't add the couple
        }
        return [...prev, coupleId];
      }
    });
  };

  // Check for duplicate events based on exact match of all event properties
  const checkDuplicateEvent = () => {
    const newEventName = generateEventName();
    const newSection = generateSection();

    console.log('🔍 Checking for duplicate:', {
      newEventName,
      newSection,
      ageGroup,
      level,
      dancerType,
      danceStyle,
      roundType,
      selectedCouples: selectedCouples.length
    });

    console.log('🔍 Available existing events:', existingEvents.map(e => ({
      name: e.name,
      section: e.section,
      ageGroup: e.ageGroup,
      level: e.level,
      dancerType: e.dancerType,
      danceStyle: e.danceStyle,
      roundType: e.roundType || e.round_type
    })));

    // Sort selected dancer IDs for consistent comparison
    const selectedDancerIds = [...selectedCouples].sort();
    const selectedDancerClothNumbers = selectedCouples
      .map(coupleId => {
        const couple = availableCouples.find(c => c.id === coupleId);
        return couple?.clothNumber;
      })
      .sort();

    // If no couples are selected, only check for duplicate event specifications
    if (selectedCouples.length === 0) {
      return existingEvents.some((event) => {
        console.log('🔍 Comparing with existing event:', {
          existingName: event.name,
          existingSection: event.section,
          existingAgeGroup: event.ageGroup || event.age_group,
          existingLevel: event.level,
          existingDancerType: event.dancerType || event.dancer_type,
          existingDanceStyle: event.danceStyle || event.dance_style
        });

        // Check event specifications match (use both camelCase and snake_case field names)
        const eventAgeGroup = event.ageGroup || event.age_group;
        const eventDancerType = event.dancerType || event.dancer_type;
        const eventDanceStyle = event.danceStyle || event.dance_style;
        const eventRoundType = event.roundType || event.round_type;

        const ageGroupMatch = eventAgeGroup === undefined || eventAgeGroup === ageGroup;
        const dancerTypeMatch = eventDancerType === undefined || eventDancerType === dancerType;
        const roundTypeMatch = eventRoundType === undefined || eventRoundType === roundType;

        const specsMatch = event.name === newEventName &&
                          event.section === newSection &&
                          ageGroupMatch &&
                          event.level === level &&
                          dancerTypeMatch &&
                          eventDanceStyle === danceStyle &&
                          roundTypeMatch;

        console.log('🔍 Specs match result:', specsMatch, 'for event:', event.name, {
          ageGroupMatch,
          dancerTypeMatch,
          roundTypeMatch,
          eventAgeGroup: eventAgeGroup,
          eventDancerType: eventDancerType,
          eventDanceStyle: eventDanceStyle,
          eventRoundType: eventRoundType
        });
        return specsMatch;
      });
    }

    return existingEvents.some((event) => {
      // Check event specifications match (use both camelCase and snake_case field names)
      const eventAgeGroup = event.ageGroup || event.age_group;
      const eventDancerType = event.dancerType || event.dancer_type;
      const eventDanceStyle = event.danceStyle || event.dance_style;
      const eventRoundType = event.roundType || event.round_type;

      const ageGroupMatch = eventAgeGroup === undefined || eventAgeGroup === ageGroup;
      const dancerTypeMatch = eventDancerType === undefined || eventDancerType === dancerType;
      const roundTypeMatch = eventRoundType === undefined || eventRoundType === roundType;

      if (event.name !== newEventName ||
          event.section !== newSection ||
          !ageGroupMatch ||
          event.level !== level ||
          !dancerTypeMatch ||
          eventDanceStyle !== danceStyle ||
          !roundTypeMatch) {
        return false;
      }

      // Handle dancers field - it might be a JSON string or array
      let eventDancers = [];
      if (event.dancers) {
        if (typeof event.dancers === 'string') {
          try {
            eventDancers = JSON.parse(event.dancers);
          } catch (error) {
            return false;
          }
        } else if (Array.isArray(event.dancers)) {
          eventDancers = event.dancers;
        }
      }

      // Check if dancer count matches
      if (eventDancers.length !== selectedCouples.length) {
        return false;
      }

      // Check if the same dancers are selected (by cloth number)
      const eventClothNumbers = eventDancers
        .map(dancer => dancer.clothNumber || dancer.cloth_number)
        .filter(clothNumber => clothNumber) // Filter out undefined cloth numbers
        .sort();

      return JSON.stringify(eventClothNumbers) === JSON.stringify(selectedDancerClothNumbers);
    });
  };

  // Get existing event that matches current configuration
  const getDuplicateEvent = () => {
    const newEventName = generateEventName();
    const newSection = generateSection();

    // Sort selected dancer cloth numbers for consistent comparison
    const selectedDancerClothNumbers = selectedCouples
      .map(coupleId => {
        const couple = availableCouples.find(c => c.id === coupleId);
        return couple?.clothNumber;
      })
      .sort();

    return existingEvents.find(event => {
      const normalizedEvent = normalizeEvent(event);

      // Check event specifications match
      if (normalizedEvent.name !== newEventName ||
          normalizedEvent.section !== newSection ||
          normalizedEvent.ageGroup !== ageGroup ||
          normalizedEvent.level !== level ||
          normalizedEvent.dancerType !== dancerType ||
          normalizedEvent.danceStyle !== danceStyle) {
        return false;
      }

      // Handle dancers field - it might be a JSON string or array
      let eventDancers = [];
      if (event.dancers) {
        if (typeof event.dancers === 'string') {
          try {
            eventDancers = JSON.parse(event.dancers);
          } catch (error) {
            console.warn('getDuplicateEvent: Failed to parse dancers JSON:', error);
            return null;
          }
        } else if (Array.isArray(event.dancers)) {
          eventDancers = event.dancers;
        }
      }

      // Check if dancer count matches
      if (eventDancers.length !== selectedCouples.length) {
        return null;
      }

      // Check if the same dancers are selected (by cloth number)
      const eventClothNumbers = eventDancers
        .map(dancer => dancer.clothNumber || dancer.cloth_number)
        .filter(clothNumber => clothNumber) // Filter out undefined cloth numbers
        .sort();

      const isDuplicate = JSON.stringify(eventClothNumbers) === JSON.stringify(selectedDancerClothNumbers);
      return isDuplicate ? normalizedEvent : null;
    });
  };

  // Real-time duplicate check for form validation
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Check for duplicates in real-time as user changes form or dancer selection
  useEffect(() => {
    // Check for duplicates if there are existing events
    // Note: Couple selection is optional, so we check for duplicates even when no couples are selected
    if (existingEvents.length > 0) {
      const hasDuplicate = checkDuplicateEvent();
      console.log('🔍 Duplicate check result:', hasDuplicate);

      if (hasDuplicate) {
        // Find the duplicate event using the same logic as checkDuplicateEvent
        let duplicateEvent = null;
        const newEventName = generateEventName();
        const newSection = generateSection();

        if (selectedCouples.length === 0) {
          // Use same logic as checkDuplicateEvent for no couples selected
          duplicateEvent = existingEvents.find(event => {
            // Check event specifications match (use both camelCase and snake_case field names)
            const eventAgeGroup = event.ageGroup || event.age_group;
            const eventDancerType = event.dancerType || event.dancer_type;
            const eventDanceStyle = event.danceStyle || event.dance_style;

            const ageGroupMatch = eventAgeGroup === undefined || eventAgeGroup === ageGroup;
            const dancerTypeMatch = eventDancerType === undefined || eventDancerType === dancerType;

            const specsMatch = event.name === newEventName &&
                              event.section === newSection &&
                              ageGroupMatch &&
                              event.level === level &&
                              dancerTypeMatch &&
                              eventDanceStyle === danceStyle;

            if (specsMatch) {
              return event; // Return raw event with item_number field
            }
            return false;
          });
        } else {
          // Use same logic as checkDuplicateEvent for couples selected
          duplicateEvent = existingEvents.find(event => {
            // Check event specifications match (use both camelCase and snake_case field names)
            const eventAgeGroup = event.ageGroup || event.age_group;
            const eventDancerType = event.dancerType || event.dancer_type;
            const eventDanceStyle = event.danceStyle || event.dance_style;

            const ageGroupMatch = eventAgeGroup === undefined || eventAgeGroup === ageGroup;
            const dancerTypeMatch = eventDancerType === undefined || eventDancerType === dancerType;

            if (event.name !== newEventName ||
                event.section !== newSection ||
                !ageGroupMatch ||
                event.level !== level ||
                !dancerTypeMatch ||
                eventDanceStyle !== danceStyle) {
              return false;
            }

            // Handle dancers field - it might be a JSON string or array
            let eventDancers = [];
            if (event.dancers) {
              if (typeof event.dancers === 'string') {
                try {
                  eventDancers = JSON.parse(event.dancers);
                } catch (error) {
                  return false;
                }
              } else if (Array.isArray(event.dancers)) {
                eventDancers = event.dancers;
              }
            }

            // Check if dancer count matches
            if (eventDancers.length !== selectedCouples.length) {
              return false;
            }

            // Check if the same dancers are selected (by cloth number)
            const selectedDancerClothNumbers = selectedCouples
              .map(coupleId => {
                const couple = availableCouples.find(c => c.id === coupleId);
                return couple?.clothNumber;
              })
              .filter(clothNumber => clothNumber)
              .sort();

            const eventClothNumbers = eventDancers
              .map(dancer => dancer.clothNumber || dancer.cloth_number)
              .filter(clothNumber => clothNumber)
              .sort();

            const isDuplicate = JSON.stringify(eventClothNumbers) === JSON.stringify(selectedDancerClothNumbers);
            if (isDuplicate) {
              return event; // Return raw event with item_number field
            }
            return false;
          });
        }

        const eventName = generateEventName();
        console.log('🚨 Duplicate found:', eventName, duplicateEvent?.name);

        // Create appropriate warning message based on whether couples are selected
        let warningMessage;
        if (selectedCouples.length > 0) {
          warningMessage = `Warning: This event configuration with these specific dancers already exists as "${eventName}". Consider modifying the event details or dancer selection.`;
        } else {
          warningMessage = `Warning: An event with these same specifications already exists as "${eventName}". Consider modifying the event details.`;
        }

        
        // Create robust warning object with all required properties
        const warningData = {
          message: warningMessage,
          itemNumber: duplicateEvent?.item_number || duplicateEvent?.itemNumber || (duplicateEvent ? 'No Number' : 'N/A'),
          existingEvent: duplicateEvent || { name: eventName }
        };

        setDuplicateWarning(warningData);
      } else {
        setDuplicateWarning(null);
      }
    } else {
      setDuplicateWarning(null);
    }
  }, [selectedCouples, ageGroup, level, dancerType, danceStyle, dances, existingEvents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const eventName = generateEventName();

      if (eventType === 'dance' && dances.length === 0) {
        throw new Error('At least one dance is required for dance events');
      }

      if (eventType === 'non-dance' && !eventName.trim()) {
        throw new Error('Event name is required for non-dance events');
      }

      // Check for duplicate event before proceeding
      console.log('🚀 FORM SUBMISSION: Checking for duplicates...');
      const isDuplicate = checkDuplicateEvent();
      console.log('🚀 FORM SUBMISSION: Duplicate check result:', isDuplicate);

      if (isDuplicate) {
        const duplicateEvent = getDuplicateEvent();
        const eventName = generateEventName();
        console.log('🚨 FORM SUBMISSION: Duplicate detected, throwing error');
        throw new Error(`Duplicate event detected! "${eventName}" with the same dancers already exists. Events cannot have identical specifications AND the same dancer combinations. Please modify the event details or dancer selection.`);
      }

      console.log('✅ FORM SUBMISSION: No duplicates found, proceeding...');

      // Calculate next item number
      const maxItemNumber = existingEvents.reduce((max, event) => {
        const itemNum = event.itemNumber || 0;
        return itemNum > max ? itemNum : max;
      }, 0);
      const nextItemNumber = maxItemNumber + 1;

      const eventData = {
        name: eventName,
        section: generateSection(),
        roundType: 'TBD', // To be determined by Scrutineer
        round_type: 'TBD', // Backend compatibility
        numHeats: 1, // Default, to be set by Scrutineer
        num_heats: 1, // Backend compatibility
        // Explicit event type field for backend
        eventType: eventType,
        // Dance event specific fields - send both camelCase and snake_case for backend compatibility
        dances: eventType === 'dance' ? dances : [],
        ageGroup: eventType === 'dance' ? ageGroup : '',
        age_group: eventType === 'dance' ? ageGroup : '',
        level: eventType === 'dance' ? level : '',
        dancerType: eventType === 'dance' ? dancerType : '',
        dancer_type: eventType === 'dance' ? dancerType : '',
        danceStyle: eventType === 'dance' ? danceStyle : '',
        dance_style: eventType === 'dance' ? danceStyle : '',
        // Non-dance event specific fields - explicitly set for clarity
        isNonDanceEvent: eventType === 'non-dance',
        is_non_dance_event: eventType === 'non-dance', // Alternative field naming for backend compatibility
        nonDanceEventType: eventType === 'non-dance' ? nonDanceEventType : '',
        customDuration: eventType === 'non-dance' ? customDuration : null,
        // Common fields - send both camelCase and snake_case for backend compatibility
        itemNumber: nextItemNumber,
        item_number: nextItemNumber,
        dancers: eventType === 'dance' ? selectedCouples.map(coupleId => {
          const couple = availableCouples.find(c => c.id === coupleId);
          const formattedName = formatDancerName(couple);

          return {
            clothNumber: couple.clothNumber,
            name: formattedName,
            country: couple.country,
            coupleId: couple.id,
            dancerType: couple.dancerType || 'couple'
          };
        }) : [], // Non-dance events don't have dancers
        competitionId: selectedCompetition?.id,
        active: false,
        currentDanceIndex: 0,
        currentHeatIndex: 0,
        createdAt: new Date().toISOString()
      };

      console.log("🎯 Creating event with data:", {
        eventType: eventType,
        isNonDanceEvent: eventData.isNonDanceEvent,
        is_non_dance_event: eventData.is_non_dance_event,
        name: eventData.name,
        section: eventData.section,
        hasDances: eventData.dances.length > 0,
        dances: eventData.dances
      });

      const response = await fetch(`https://${_host}:6003/api/${_company}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      // If server doesn't support competitionId yet, it will still work
      // The frontend handles this gracefully

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create event');
      }

      const createdEvent = await response.json();
      console.log("Event created successfully:", createdEvent);

      // Log event creation
      if (currentUser && createdEvent.id) {
        auditLogger.logCreateEvent(
          currentUser.username || currentUser.id,
          eventName,
          createdEvent.id
        );
      }

      setSuccess(true);
      setTimeout(() => {
        resetForm();
        setShowForm(false);
        setSuccess(false);

        if (onCreateEvent) {
          onCreateEvent(createdEvent);
        }
      }, 1500);
    } catch (err) {
      console.error("Error creating event:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="event-form-container">
      <button
        className="toggle-form-btn"
        onClick={toggleForm}
      >
        {showForm ? 'Cancel' : 'Create New Event'}
      </button>

      {showForm && (
        <form className="event-form" onSubmit={handleSubmit}>
          <h2>Create New Event</h2>

          {/* Event Type Selection */}
          <div className="event-type-section">
            <h3>Event Type</h3>
            <div className="form-group">
              <label htmlFor="eventType">Choose Event Type:</label>
              <select
                id="eventType"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
              >
                <option value="dance">Dance Event</option>
                <option value="non-dance">Non-Dance Event (Break/Lunch/Entertainment)</option>
              </select>
            </div>
          </div>

          {/* Event Name Components */}
          <div className="event-name-section">
            <h3>Event Name</h3>
            <div className="event-name-preview">
              {generateEventName()}
            </div>

            {/* Duplicate Warning */}
            {duplicateWarning && (
              <div className="duplicate-warning">
                <div className="warning-icon">⚠️</div>
                <div className="warning-content">
                  <div className="warning-message">{duplicateWarning.message}</div>
                  <div className="existing-event-info">
                    Existing: Event #{duplicateWarning.itemNumber} - {duplicateWarning.existingEvent?.name || 'Unknown Event'}
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Section:</label>
              <div className="section-preview">
                {generateSection()}
              </div>
            </div>
          </div>

          {/* Conditional Event Configuration */}
          {eventType === 'dance' ? (
            /* Dance Event Configuration */
            <div className="dance-event-configuration">
              <h3>Dance Event Details</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ageGroup">Age Group:</label>
                  <select
                    id="ageGroup"
                    value={ageGroup}
                    onChange={(e) => setAgeGroup(e.target.value)}
                  >
                    {AGE_GROUPS.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="level">Level:</label>
                  <select
                    id="level"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                  >
                    {LEVELS.map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {lvl}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dancerType">Type:</label>
                  <select
                    id="dancerType"
                    value={dancerType}
                    onChange={(e) => setDancerType(e.target.value)}
                  >
                    {DANCER_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="danceStyle">Style:</label>
                  <select
                    id="danceStyle"
                    value={danceStyle}
                    onChange={(e) => setDanceStyle(e.target.value)}
                  >
                    {DANCE_STYLES.map((style) => (
                      <option key={style} value={style}>
                        {style}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group dances-group">
                <label>Dances:</label>
                <ul className="dance-list">
                  {dances.map((dance, index) => (
                    <li key={index} className="dance-item">
                      {dance}
                      <button
                        type="button"
                        className="remove-dance-btn"
                        onClick={() => handleRemoveDance(index)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="add-dance-container">
                  <input
                    type="text"
                    value={newDance}
                    onChange={(e) => setNewDance(e.target.value)}
                    placeholder="Add a dance..."
                  />
                  <button
                    type="button"
                    className="add-dance-btn"
                    onClick={handleAddDance}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="form-group couples-selection">
                <label>Select Couples (Optional):</label>
                <p className="allocation-hint">
                  You can assign dancers later via Event/Dancer Allocation<br/>
                  <strong>Note:</strong> Dancers can only be allocated to one event per dancer type and style combination
                </p>
                {availableCouples.length === 0 ? (
                  <p className="no-couples-message">
                    No dancers available. <a href="/administration">Go to Administration</a> to add dancers first.
                  </p>
                ) : (
                  <>
                    <input
                      type="text"
                      className="couple-search-input"
                      placeholder="Search by cloth number or name..."
                      value={coupleSearch}
                      onChange={(e) => setCoupleSearch(e.target.value)}
                    />
                    <div className="couples-grid">
                      {filteredCouples.length === 0 ? (
                        <p className="no-couples-message">
                          {coupleSearch
                            ? 'No dancers found matching your search.'
                            : 'No available dancers for this dancer type and style combination.'
                          }
                        </p>
                      ) : (
                        <>
                          {filteredCouples.map(couple => (
                          <div
                            key={couple.id}
                            className={`couple-item ${selectedCouples.includes(couple.id) ? 'selected' : ''}`}
                            onClick={() => handleCoupleToggle(couple.id)}
                          >
                            <div className="couple-number">{couple.clothNumber}</div>
                            <div className="couple-details">
                              <div className="couple-names">
                                {formatDancerName(couple)}
                              </div>
                              <div className="couple-country">{couple.country}</div>
                            </div>
                          </div>
                        ))}

                          {/* Show conflicted dancers (not available for selection) */}
                          {availableCouples.filter(couple => !getFilteredAvailableCouples().includes(couple)).length > 0 && (
                            <div className="conflicted-dancers-section">
                              <p className="conflicted-dancers-title">Already allocated to similar events:</p>
                              {availableCouples.filter(couple => !getFilteredAvailableCouples().includes(couple))
                                .filter(couple => {
                                  const searchLower = coupleSearch.toLowerCase();
                                  const name = formatDancerName(couple).toLowerCase();
                                  const clothNumber = couple.clothNumber?.toString() || '';
                                  return name.includes(searchLower) || clothNumber.includes(searchLower);
                                })
                                .map(couple => {
                                  const conflict = checkAllocationConflict(couple.id);
                                  return (
                                    <div key={couple.id} className="couple-item conflicted">
                                      <div className="couple-number">{couple.clothNumber}</div>
                                      <div className="couple-details">
                                        <div className="couple-names">
                                          {formatDancerName(couple)}
                                        </div>
                                        <div className="couple-country">{couple.country}</div>
                                        <div className="conflict-info">
                                          Already in: {conflict}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            /* Non-Dance Event Configuration */
            <div className="non-dance-event-configuration">
              <h3>Non-Dance Event Details</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nonDanceEventType">Event Type:</label>
                  <select
                    id="nonDanceEventType"
                    value={nonDanceEventType}
                    onChange={(e) => setNonDanceEventType(e.target.value)}
                  >
                    {NON_DANCE_EVENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="customDuration">Duration (minutes):</label>
                  <input
                    id="customDuration"
                    type="number"
                    min="1"
                    max="480" // Max 8 hours
                    value={customDuration}
                    onChange={(e) => setCustomDuration(parseInt(e.target.value, 10) || 1)}
                  />
                  <small>Duration of the event in minutes</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="nonDanceEventName">Custom Event Name:</label>
                <input
                  id="nonDanceEventName"
                  type="text"
                  value={nonDanceEventName}
                  onChange={(e) => setNonDanceEventName(e.target.value)}
                  placeholder="Enter custom event name (optional)"
                />
                <small>Leave blank to use default name based on event type</small>
              </div>

              <div className="non-dance-info">
                <p><strong>Note:</strong> Non-dance events will appear in the running order with their custom duration and can be moved up/down like regular events.</p>
              </div>
            </div>
          )}

          {duplicateWarning && <div className="warning-message">{duplicateWarning.message}</div>}
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">Event created successfully!</div>}

          <button
            type="submit"
            className="submit-btn"
            disabled={loading || duplicateWarning}
            title={duplicateWarning ? 'Cannot create duplicate event' : 'Create new event'}
          >
            {loading ? 'Creating...' : duplicateWarning ? 'Duplicate Event' : 'Create Event'}
          </button>
        </form>
      )}
    </div>
  );
};

export default EventForm;
